import { useEffect, useState } from "react";
import { Check, ImagePlus, Lightbulb, Paperclip, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, DOMAINES, domaineLabel, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const blank = { titre: "", domaine: "electricite", texte: "", video_url: "", images: [], tags: "", materiel: "", publie: true };

export const imageSrc = (url) => (!url ? null : url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);

const Galerie = ({ images, onChange }) => {
  const [busy, setBusy] = useState(false);

  const envoyer = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/tutorials/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.image_url);
      }
      onChange([...(images || []), ...urls]);
      toast.success(`${urls.length} photo(s) ajoutée(s)`);
    } catch (err) {
      toast.error(errMsg(err, "Envoi des photos impossible"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="tuto-galerie">
      <div className="bk-label flex items-center gap-2">
        <ImagePlus size={13} /> Photos d'illustration
      </div>
      {images?.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {images.map((url) => (
            <div key={url} className="relative group" data-testid={`tuto-image-${url.split("/").pop()}`}>
              <img src={imageSrc(url)} alt="" className="h-16 w-full object-cover rounded-lg border border-slate-200" />
              <button
                data-testid={`tuto-image-remove-${url.split("/").pop()}`}
                onClick={() => onChange(images.filter((u) => u !== url))}
                className="absolute -top-1.5 -right-1.5 bg-white rounded-full border border-slate-200 p-1 text-slate-400 hover:text-red-500 shadow-sm"
              >
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
      <input data-testid="tuto-images-input" type="file" accept="image/*" multiple onChange={envoyer} className="block w-full text-sm text-slate-600" />
      {busy && <p className="text-xs text-slate-500">Envoi en cours…</p>}
      <p className="text-xs text-slate-400">Les photos apparaissent dans le tutoriel côté client (visibles après enregistrement).</p>
    </div>
  );
};

export default function AdminTutos() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [medias, setMedias] = useState([]);
  const [file, setFile] = useState(null);
  const [besoins, setBesoins] = useState(null);
  const [filtreLacunes, setFiltreLacunes] = useState(true);

  const load = () => api.get("/admin/tutorials").then(({ data }) => setItems(data));
  const loadBesoins = (sansResultat = filtreLacunes) =>
    api.get("/admin/tutorials/searches", { params: { jours: 90, sans_resultat: sansResultat } }).then(({ data }) => setBesoins(data));
  const loadMedias = (id) => api.get("/admin/documents", { params: { tutorial_id: id, categorie: "tuto" } }).then(({ data }) => setMedias(data));

  useEffect(() => {
    load();
    loadBesoins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const depuisRecherche = (r) => {
    setEditId(null);
    setMedias([]);
    setForm({ ...blank, titre: r.question.charAt(0).toUpperCase() + r.question.slice(1), domaine: r.domaine_probable || "electricite", tags: r.question.split(" ").filter((w) => w.length > 4).slice(0, 4).join(", "), publie: false });
    toast.success("Brouillon pré-rempli depuis la recherche client");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const traiter = async (r) => {
    await api.post(`/admin/tutorials/searches/${r.id}/traite`);
    loadBesoins();
  };

  const uploadMedia = async () => {
    if (!editId) return toast.error("Enregistrez d'abord le tutoriel");
    if (!file) return toast.error("Choisissez un fichier");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("categorie", "tuto");
    fd.append("titre", file.name);
    fd.append("tutorial_id", editId);
    try {
      await api.post("/admin/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Média ajouté au tutoriel");
      setFile(null);
      loadMedias(editId);
    } catch (e) {
      toast.error(errMsg(e, "Dépôt impossible"));
    }
  };

  const save = async () => {
    if (!form.titre.trim()) return toast.error("Titre requis");
    const payload = {
      ...form,
      images: form.images || [],
      tags: form.tags ? String(form.tags).split(",").map((t) => t.trim()).filter(Boolean) : [],
      materiel: form.materiel ? String(form.materiel).split(",").map((t) => t.trim()).filter(Boolean) : [],
    };
    if (editId) await api.put(`/admin/tutorials/${editId}`, payload);
    else await api.post("/admin/tutorials", payload);
    toast.success("Tutoriel enregistré");
    setForm(blank);
    setEditId(null);
    load();
  };

  const edit = (t) => {
    setEditId(t.id);
    setForm({ ...t, images: t.images || [], tags: (t.tags || []).join(", "), materiel: (t.materiel || []).join(", ") });
    loadMedias(t.id);
  };

  const remove = async (t) => {
    await api.delete(`/admin/tutorials/${t.id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-tutos">
      <div>
        <div className="bk-label mb-2">CMS</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">Tutoriels</h1>
      </div>

      {besoins && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-5 space-y-4" data-testid="tutos-besoins">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="bk-label flex items-center gap-2 text-amber-700">
                <Lightbulb size={14} /> Contenus à créer — recherches clients (90 j)
              </div>
              <p className="text-sm text-slate-700 mt-2">
                <span className="font-semibold">{besoins.sans_resultat}</span> recherche(s) sans tutoriel réellement pertinent sur {besoins.total} au total.
                {besoins.lacunes_par_domaine.length > 0 && (
                  <span className="text-slate-600">
                    {" "}Domaines concernés : {besoins.lacunes_par_domaine.map(([d, n]) => `${d === "indetermine" ? "indéterminé" : domaineLabel(d)} (${n})`).join(", ")}.
                  </span>
                )}
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid="besoins-filtre-toggle">
              <input
                type="checkbox"
                checked={filtreLacunes}
                onChange={(e) => {
                  setFiltreLacunes(e.target.checked);
                  loadBesoins(e.target.checked);
                }}
                className="accent-[#005CFF]"
              />
              Uniquement les recherches sans résultat pertinent
            </label>
          </div>

          <div className="space-y-2">
            {besoins.recherches.map((r) => (
              <div key={r.id} data-testid={`besoin-row-${r.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white border border-amber-100 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-900">« {r.question} »</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {r.domaine_probable ? domaineLabel(r.domaine_probable) : "domaine indéterminé"} · {r.nb_tutos} tuto(s) proposé(s)
                    {r.nb_tutos > 0 ? ` · meilleure pertinence ${r.pertinence_max}%` : ""} · {fmtDate(r.created_at)}
                    {r.lacune && <span className="text-amber-700 font-semibold"> · à couvrir</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button data-testid={`besoin-create-${r.id}`} onClick={() => depuisRecherche(r)} size="sm" className="bg-slate-900 hover:bg-[#005CFF]">
                    <Plus size={14} className="mr-1" /> Créer le tuto
                  </Button>
                  <button data-testid={`besoin-traite-${r.id}`} onClick={() => traiter(r)} title="Marquer comme traité" className="p-2 text-slate-400 hover:text-emerald-600">
                    <Check size={16} />
                  </button>
                </div>
              </div>
            ))}
            {besoins.recherches.length === 0 && (
              <p className="text-sm text-slate-600" data-testid="besoins-empty">Aucune recherche en attente : le catalogue couvre les demandes clients récentes.</p>
            )}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
          <div className="bk-label">{editId ? "Modifier" : "Nouveau tutoriel"}</div>
          <Input data-testid="tuto-titre-input" value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} placeholder="Titre" className="h-11" />
          <select
            data-testid="tuto-domaine-select"
            value={form.domaine}
            onChange={(e) => setForm({ ...form, domaine: e.target.value })}
            className="w-full h-11 border border-slate-200 rounded-lg px-3 text-sm"
          >
            {DOMAINES.map((d) => (
              <option key={d.key} value={d.key}>{d.label}</option>
            ))}
          </select>
          <Textarea data-testid="tuto-texte-input" value={form.texte} onChange={(e) => setForm({ ...form, texte: e.target.value })} rows={5} placeholder="Process pas à pas" />
          <Input data-testid="tuto-video-input" value={form.video_url || ""} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="Lien vidéo (YouTube/Vimeo)" className="h-11" />
          <Galerie images={form.images} onChange={(images) => setForm({ ...form, images })} />
          <Input data-testid="tuto-tags-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Tags (séparés par des virgules)" className="h-11" />
          <Input data-testid="tuto-materiel-input" value={form.materiel} onChange={(e) => setForm({ ...form, materiel: e.target.value })} placeholder="Matériel concerné (virgules)" className="h-11" />
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={form.publie} onChange={(e) => setForm({ ...form, publie: e.target.checked })} className="accent-[#005CFF]" /> Publié
          </label>
          <div className="flex gap-2">
            <Button data-testid="tuto-save-btn" onClick={save} className="bg-[#005CFF] hover:bg-[#0047CC]">
              <Plus size={15} className="mr-1" /> Enregistrer
            </Button>
            {editId && (
              <Button variant="outline" onClick={() => { setEditId(null); setForm(blank); setMedias([]); }}>Annuler</Button>
            )}
          </div>

          <div className="pt-3 border-t border-slate-100 space-y-2">
            <div className="bk-label flex items-center gap-2">
              <Paperclip size={13} /> Pièces jointes (PDF, photo, GIF, mini-vidéo…)
            </div>
            {!editId && <p className="text-xs text-slate-500">Enregistrez le tutoriel puis rouvrez-le pour ajouter des documents.</p>}
            {editId && (
              <>
                <input data-testid="tuto-media-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm text-slate-600" />
                <Button data-testid="tuto-media-upload-btn" onClick={uploadMedia} variant="outline" size="sm">
                  <Upload size={14} className="mr-1" /> Ajouter le média
                </Button>
                <div className="space-y-1 pt-1">
                  {medias.map((m) => (
                    <div key={m.id} data-testid={`tuto-media-${m.id}`} className="flex items-center justify-between text-xs text-slate-600 border-b border-slate-100 pb-1">
                      <span className="truncate">{m.filename} · {Math.round((m.size || 0) / 1024)} Ko</span>
                      <button
                        onClick={async () => {
                          await api.delete(`/admin/documents/${m.id}`);
                          loadMedias(editId);
                        }}
                        className="text-slate-300 hover:text-red-500"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                  {medias.length === 0 && <p className="text-xs text-slate-400">Aucune pièce jointe.</p>}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {items.map((t) => (
            <div key={t.id} data-testid={`admin-tuto-${t.id}`} className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-start justify-between gap-3">
              <button onClick={() => edit(t)} className="text-left min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{t.titre}</div>
                <div className="text-xs text-slate-500">{domaineLabel(t.domaine)} · {t.publie ? "publié" : "brouillon"}</div>
              </button>
              <button data-testid={`admin-tuto-delete-${t.id}`} onClick={() => remove(t)} className="text-slate-300 hover:text-red-500">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
